import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/offerings", label: "Offerings" },
    { path: "/book", label: "Book" },
    { path: "/ethics", label: "Ethics" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled || isMobileMenuOpen
          ? "backdrop-blur-md bg-bg-primary/95 border-b border-white/5"
          : "bg-bg-primary/80 md:bg-transparent"
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-heading text-2xl text-text-primary hover:text-brand-accent transition-colors duration-300"
          >
            Somale
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`font-body text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-brand-accent"
                    : "text-text-secondary hover:text-brand-accent"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <Link
            to="/book"
            data-testid="nav-cta-button"
            className="hidden md:block btn-primary"
          >
            Work With Me
          </Link>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-text-primary p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-bg-secondary border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="section-container py-6 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`font-body text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-brand-accent"
                      : "text-text-secondary hover:text-brand-accent"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/book"
                data-testid="mobile-nav-cta"
                className="btn-primary text-center mt-4"
              >
                Work With Me
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
